import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { useAdminContent } from '@/hooks/useAdminContent';
import {
  Loader2, ArrowLeft, ExternalLink, RefreshCw, Save,
  Plus, X, Eye, FileText
} from 'lucide-react';

const AdminEditPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [password] = useState(() => sessionStorage.getItem('admin_password') || '');
  const [activeTab, setActiveTab] = useState('content');

  // Redirect if not authenticated
  useEffect(() => {
    if (!password) {
      navigate('/admin');
    }
  }, [password, navigate]);

  const {
    loading, saving, regenerating, publishing,
    template, content, score,
    updateField, handleSave, handleRegenerate,
    handlePublish, handleUnpublish, handleStatusChange,
    addListItem, updateListItem, removeListItem,
    addFaq, updateFaq, removeFaq
  } = useAdminContent(slug, password, navigate);

  // Redirect if not authenticated
  useEffect(() => {
    if (!password) {
      navigate('/admin');
    }
  }, [password, navigate]);

  const scoreColor = score >= 80 ? '#22c55e' : score >= 60 ? '#eab308' : '#ef4444';
  const scoreLabel = score >= 80 ? 'Ready to publish' : score >= 60 ? 'Needs improvement' : 'Incomplete';

  if (loading) {
    return (
      <div className="min-h-screen bg-[#09090b] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#bef264]" />
      </div>
    );
  }

  if (!template || !content) return null;

  return (
    <div className="min-h-screen bg-[#09090b]">
      <div className="flex" style={{ minHeight: '100vh' }}>

        {/* LEFT SIDEBAR */}
        <div className="w-[280px] flex-shrink-0 bg-[#111113] border-r border-[#27272a] p-5 sticky top-0 h-screen overflow-y-auto">
          <img
            src={template.thumbnail_url || template.url}
            alt={template.name}
            className="w-full rounded-lg border border-[#27272a] mb-4"
          />
          <h2 className="text-lg font-bold text-white font-['Space_Grotesk'] mb-2">{template.name}</h2>
          <div className="flex gap-2 mb-4">
            <Badge className="bg-[#27272a] text-[#a1a1aa]">{template.category}</Badge>
            <Badge className="bg-[#27272a] text-[#bef264]">#{template.imgflip_rank || '-'}</Badge>
          </div>

          {/* Score Card */}
          <div className="bg-[#18181b] border border-[#27272a] rounded-lg p-4 mb-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-[#a1a1aa]">Completion</span>
              <span className="text-2xl font-bold" style={{ color: scoreColor }}>{score}</span>
            </div>
            <div className="w-full h-2 bg-[#27272a] rounded-full overflow-hidden mb-2">
              <div
                className="h-full rounded-full transition-all duration-500"
                style={{ width: `${score}%`, background: scoreColor }}
              />
            </div>
            <span className="text-xs" style={{ color: scoreColor }}>{scoreLabel}</span>
          </div>

          {/* Status Selector */}
          <div className="mb-4">
            <label className="text-xs text-[#a1a1aa] block mb-1">Pipeline Status</label>
            <select
              value={template.pipeline_status || 'SCRAPED'}
              onChange={(e) => handleStatusChange(e.target.value)}
              className="w-full bg-[#09090b] border border-[#27272a] text-white rounded-md px-3 py-2 text-sm"
              data-testid="status-selector"
            >
              <option value="CONTENT_GENERATED">CONTENT_GENERATED</option>
              <option value="CURATING">CURATING</option>
              <option value="READY">READY</option>
              <option value="PUBLISHED">PUBLISHED</option>
              <option value="SKIPPED">SKIPPED</option>
            </select>
          </div>

          {/* Action Buttons */}
          <div className="space-y-2">
            <Button
              onClick={handleRegenerate}
              disabled={regenerating}
              variant="outline"
              className="w-full border-[#27272a] text-white hover:bg-white/5"
              data-testid="regenerate-btn"
            >
              {regenerating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
              Regenerate AI Content
            </Button>
            {template.pipeline_status === 'PUBLISHED' ? (
              <Button
                onClick={handleUnpublish}
                disabled={publishing}
                variant="outline"
                className="w-full border-red-900 text-red-400 hover:bg-red-900/20"
                data-testid="unpublish-btn"
              >
                {publishing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                Unpublish
              </Button>
            ) : (
              <Button
                onClick={handlePublish}
                disabled={publishing || score < 60}
                className="w-full bg-[#bef264] text-black hover:bg-[#a3e635] disabled:opacity-40"
                data-testid="publish-btn"
              >
                {publishing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                Publish Now
              </Button>
            )}
            <Button
              onClick={() => navigate('/admin')}
              variant="outline"
              className="w-full border-[#27272a] text-[#a1a1aa] hover:text-white hover:bg-white/5"
              data-testid="back-to-queue-btn"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Queue
            </Button>
            {template.pipeline_status === 'PUBLISHED' && (
              <a
                href={`/api/seo/meme/${template.slug}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-2 w-full py-2 text-sm text-[#bef264] hover:underline"
              >
                View Live Page <ExternalLink className="w-3 h-3" />
              </a>
            )}
          </div>
        </div>

        {/* RIGHT MAIN AREA */}
        <div className="flex-1 p-6 overflow-y-auto">
          {/* Tabs */}
          <div className="flex gap-1 mb-6 bg-[#18181b] rounded-lg p-1 w-fit border border-[#27272a]">
            <button
              onClick={() => setActiveTab('content')}
              className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                activeTab === 'content'
                  ? 'bg-[#bef264] text-black'
                  : 'text-[#a1a1aa] hover:text-white'
              }`}
              data-testid="tab-content"
            >
              <FileText className="w-4 h-4" /> Content
            </button>
            <button
              onClick={() => setActiveTab('seo')}
              className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                activeTab === 'seo'
                  ? 'bg-[#bef264] text-black'
                  : 'text-[#a1a1aa] hover:text-white'
              }`}
              data-testid="tab-seo"
            >
              <Eye className="w-4 h-4" /> SEO Preview
            </button>
          </div>

          {activeTab === 'content' && (
            <div className="space-y-5 max-w-3xl">
              {/* SEO Title */}
              <div>
                <div className="flex justify-between mb-1">
                  <label className="text-sm font-medium text-[#a1a1aa]">SEO Title</label>
                  <span className={`text-xs ${(content.seo_title || '').length > 60 ? 'text-red-400' : 'text-[#52525b]'}`}>
                    {(content.seo_title || '').length}/60
                  </span>
                </div>
                <Input
                  value={content.seo_title || ''}
                  onChange={(e) => updateField('seo_title', e.target.value)}
                  className="bg-[#18181b] border-[#27272a] text-white"
                  data-testid="field-seo-title"
                />
              </div>

              {/* Meta Description */}
              <div>
                <div className="flex justify-between mb-1">
                  <label className="text-sm font-medium text-[#a1a1aa]">Meta Description</label>
                  <span className={`text-xs ${(content.meta_description || '').length > 160 ? 'text-red-400' : 'text-[#52525b]'}`}>
                    {(content.meta_description || '').length}/160
                  </span>
                </div>
                <textarea
                  value={content.meta_description || ''}
                  onChange={(e) => updateField('meta_description', e.target.value)}
                  rows={2}
                  className="w-full bg-[#18181b] border border-[#27272a] text-white rounded-md px-3 py-2 text-sm resize-none focus:outline-none focus:ring-1 focus:ring-[#bef264]"
                  data-testid="field-meta-description"
                />
              </div>

              {/* H1 */}
              <div>
                <label className="text-sm font-medium text-[#a1a1aa] block mb-1">H1</label>
                <Input
                  value={content.h1 || ''}
                  onChange={(e) => updateField('h1', e.target.value)}
                  className="bg-[#18181b] border-[#27272a] text-white"
                  data-testid="field-h1"
                />
              </div>

              {/* Origin Story */}
              <div>
                <label className="text-sm font-medium text-[#a1a1aa] block mb-1">Origin & History</label>
                <textarea
                  value={content.origin_story || ''}
                  onChange={(e) => updateField('origin_story', e.target.value)}
                  rows={8}
                  className="w-full bg-[#18181b] border border-[#27272a] text-white rounded-md px-3 py-2 text-sm resize-vertical focus:outline-none focus:ring-1 focus:ring-[#bef264]"
                  data-testid="field-origin-story"
                />
              </div>

              {/* How To Use */}
              <div>
                <label className="text-sm font-medium text-[#a1a1aa] block mb-1">How To Use</label>
                <textarea
                  value={content.how_to_use || ''}
                  onChange={(e) => updateField('how_to_use', e.target.value)}
                  rows={4}
                  className="w-full bg-[#18181b] border border-[#27272a] text-white rounded-md px-3 py-2 text-sm resize-vertical focus:outline-none focus:ring-1 focus:ring-[#bef264]"
                  data-testid="field-how-to-use"
                />
              </div>

              {/* Fun Facts */}
              <div>
                <div className="flex justify-between items-center mb-2">
                  <label className="text-sm font-medium text-[#a1a1aa]">Fun Facts</label>
                  <Button onClick={() => addListItem('fun_facts')} size="sm" variant="outline" className="border-[#27272a] text-[#a1a1aa] hover:text-white h-7 text-xs">
                    <Plus className="w-3 h-3 mr-1" /> Add Fact
                  </Button>
                </div>
                <div className="space-y-2">
                  {(content.fun_facts || []).map((fact, i) => (
                    <div key={`fact-${i}`} className="flex gap-2">
                      <Input
                        value={fact}
                        onChange={(e) => updateListItem('fun_facts', i, e.target.value)}
                        className="bg-[#18181b] border-[#27272a] text-white flex-1"
                      />
                      <Button onClick={() => removeListItem('fun_facts', i)} size="sm" variant="ghost" className="text-red-400 hover:text-red-300 px-2">
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>

              {/* Example Captions */}
              <div>
                <div className="flex justify-between items-center mb-2">
                  <label className="text-sm font-medium text-[#a1a1aa]">
                    Example Captions <span className="text-[#52525b]">({(content.example_captions || []).length} captions)</span>
                  </label>
                  <Button onClick={() => addListItem('example_captions')} size="sm" variant="outline" className="border-[#27272a] text-[#a1a1aa] hover:text-white h-7 text-xs">
                    <Plus className="w-3 h-3 mr-1" /> Add Caption
                  </Button>
                </div>
                <div className="space-y-2">
                  {(content.example_captions || []).map((cap, i) => (
                    <div key={`cap-${i}`} className="flex gap-2">
                      <Input
                        value={cap}
                        onChange={(e) => updateListItem('example_captions', i, e.target.value)}
                        className="bg-[#18181b] border-[#27272a] text-white flex-1"
                      />
                      <Button onClick={() => removeListItem('example_captions', i)} size="sm" variant="ghost" className="text-red-400 hover:text-red-300 px-2">
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>

              {/* Related Templates */}
              <div>
                <label className="text-sm font-medium text-[#a1a1aa] block mb-2">Related Templates</label>
                <div className="flex flex-wrap gap-2 mb-2">
                  {(content.related_slugs || []).map((rs, i) => (
                    <span key={`rel-${rs}`} className="flex items-center gap-1 bg-[#27272a] text-[#d4d4d8] px-3 py-1 rounded-full text-xs">
                      {rs}
                      <button onClick={() => removeListItem('related_slugs', i)} className="text-red-400 hover:text-red-300 ml-1">
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                </div>
                <div className="flex gap-2">
                  <Input
                    placeholder="Add slug (e.g. drake-hotline-bling)"
                    className="bg-[#18181b] border-[#27272a] text-white"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && e.target.value.trim()) {
                        addListItem('related_slugs');
                        updateListItem('related_slugs', (content.related_slugs || []).length, e.target.value.trim());
                        e.target.value = '';
                      }
                    }}
                    data-testid="add-related-slug-input"
                  />
                </div>
              </div>

              {/* Save Button */}
              <div className="pt-4 border-t border-[#27272a]">
                <Button
                  onClick={handleSave}
                  disabled={saving}
                  className="bg-[#bef264] text-black hover:bg-[#a3e635]"
                  data-testid="save-changes-btn"
                >
                  {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                  Save All Changes
                </Button>
              </div>
            </div>
          )}

          {activeTab === 'seo' && (
            <div className="max-w-3xl space-y-6">
              {/* Google Preview */}
              <div>
                <h3 className="text-sm font-medium text-[#a1a1aa] mb-3">Google Search Preview</h3>
                <div className="bg-white rounded-lg p-5 max-w-xl">
                  <div className="text-[#1a0dab] text-xl hover:underline cursor-pointer" style={{ fontFamily: 'Arial, sans-serif' }}>
                    {content.seo_title || 'No SEO title set'}
                  </div>
                  <div className="text-[#006621] text-sm mt-1" style={{ fontFamily: 'Arial, sans-serif' }}>
                    memestudio.com/meme/{slug}
                  </div>
                  <div className="text-[#545454] text-sm mt-1 line-clamp-2" style={{ fontFamily: 'Arial, sans-serif' }}>
                    {content.meta_description || 'No meta description set'}
                  </div>
                </div>
              </div>

              {/* FAQ Preview */}
              <div>
                <div className="flex justify-between items-center mb-3">
                  <h3 className="text-sm font-medium text-[#a1a1aa]">FAQ Items</h3>
                  <Button onClick={addFaq} size="sm" variant="outline" className="border-[#27272a] text-[#a1a1aa] hover:text-white h-7 text-xs">
                    <Plus className="w-3 h-3 mr-1" /> Add FAQ Question
                  </Button>
                </div>
                <div className="space-y-3">
                  {(content.faq || []).map((faq, i) => (
                    <div key={`faq-${i}`} className="bg-[#18181b] border border-[#27272a] rounded-lg p-4">
                      <div className="flex items-start gap-2 mb-2">
                        <Input
                          value={faq.question}
                          onChange={(e) => updateFaq(i, 'question', e.target.value)}
                          placeholder="Question"
                          className="bg-[#09090b] border-[#27272a] text-white flex-1"
                        />
                        <Button onClick={() => removeFaq(i)} size="sm" variant="ghost" className="text-red-400 hover:text-red-300 px-2">
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                      <textarea
                        value={faq.answer}
                        onChange={(e) => updateFaq(i, 'answer', e.target.value)}
                        placeholder="Answer"
                        rows={2}
                        className="w-full bg-[#09090b] border border-[#27272a] text-white rounded-md px-3 py-2 text-sm resize-none focus:outline-none focus:ring-1 focus:ring-[#bef264]"
                      />
                    </div>
                  ))}
                  {(content.faq || []).length === 0 && (
                    <p className="text-[#52525b] text-sm py-4 text-center">No FAQ items yet. Default FAQs will be shown on the page.</p>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminEditPage;
